/**
 * Console elevation kit — the "enterprise, but beautiful" layer for the staff
 * console. Hero bands with accent gradients + starfield, metric tiles with
 * icon badges, refined section cards, and a sliding-thumb segmented tab
 * control (accessible: real role=tab buttons, roving tabindex, arrow keys).
 */
import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

/* Console motion tokens (ms) — one transition language for the staff app. */
export const MOTION = {
  fast: 200,
  base: 300,
  slow: 450,
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
} as const;

/* --------------------------------------------------------------------------
 * HeroBand — the page hero: deep gradient, starfield, accent bloom, and a
 * content slot (title/subtitle/status/actions/metrics).
 * ------------------------------------------------------------------------- */
export function HeroBand({
  eyebrow,
  title,
  subtitle,
  status,
  actions,
  metrics,
  accent = '#0173ab',
  breadcrumbs,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  status?: ReactNode;
  actions?: ReactNode;
  metrics?: ReactNode;
  /** Primary accent color used for the glow + eyebrow text. */
  accent?: string;
  breadcrumbs?: ReactNode;
}) {
  return (
    <header className="relative mb-6 overflow-hidden rounded-[1.5rem] text-white shadow-[0_28px_60px_-30px_rgb(18_42_56/0.55)]">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: `linear-gradient(125deg, #1b1d21 0%, #12343f 55%, ${accent} 140%)`,
        }}
      />
      <div aria-hidden className="starfield absolute inset-0 opacity-60" />
      <div
        aria-hidden
        className="absolute -end-24 -top-32 h-80 w-80 rounded-full blur-[100px]"
        style={{ backgroundColor: `${accent}59` }}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: 'linear-gradient(to right, transparent, rgb(255 255 255 / 0.55), transparent)' }}
      />
      <div className="relative px-6 pb-5 pt-5 sm:px-8">
        {breadcrumbs && <div className="mb-3 text-[11px] font-semibold text-white/60">{breadcrumbs}</div>}
        {eyebrow && (
          <div className="mb-2 text-[10px] font-extrabold uppercase tracking-[0.2em] text-aegold-200">
            {eyebrow}
          </div>
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-black tracking-tight sm:text-3xl">{title}</h1>
            {subtitle && <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-white/70">{subtitle}</p>}
          </div>
          {(status || actions) && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {status}
              {actions}
            </div>
          )}
        </div>
        {metrics && <div className="mt-5">{metrics}</div>}
      </div>
    </header>
  );
}

/* --------------------------------------------------------------------------
 * IconBadge — rounded gradient icon chip.
 * ------------------------------------------------------------------------- */
export function IconBadge({
  children,
  accent = '#0173ab',
  size = 40,
}: {
  children: ReactNode;
  accent?: string;
  size?: number;
}) {
  return (
    <span
      aria-hidden
      className="grid shrink-0 place-items-center rounded-2xl text-white shadow-[0_8px_20px_-8px_rgb(18_42_56/0.5)]"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(140deg, ${accent}, ${accent}b3)`,
      }}
    >
      {children}
    </span>
  );
}

/* --------------------------------------------------------------------------
 * MetricTile — elevated metric: icon badge, big number, label, hint, and an
 * optional tone bar. Reads beautifully on the hero band (dark) or on cards.
 * ------------------------------------------------------------------------- */
export function MetricTile({
  label,
  value,
  hint,
  icon,
  accent = '#cba344',
  dark = false,
  spark,
  delta,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  icon?: ReactNode;
  accent?: string;
  dark?: boolean;
  /** Optional sparkline values (6–24 numbers) rendered as a tiny SVG trend. */
  spark?: number[];
  /** Optional delta text (e.g. '+12%') shown next to the value. */
  delta?: { text: string; tone: 'up' | 'down' | 'flat' };
}) {
  return (
    <div
      className={
        dark
          ? 'rounded-2xl border border-white/15 bg-white/8 p-4 backdrop-blur-sm'
          : 'rounded-2xl border border-aeblack-100 bg-white p-4 shadow-[0_2px_10px_-4px_rgb(14_15_18/0.08)]'
      }
    >
      <div className="flex items-center gap-3">
        {icon && <IconBadge accent={accent} size={36}>{icon}</IconBadge>}
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] font-extrabold uppercase tracking-wide ${dark ? 'text-white/60' : 'text-ink-faint'}`}>
            {label}
          </div>
          <div className={`mt-0.5 flex items-baseline gap-1.5 text-xl font-black ${dark ? 'text-white' : 'text-ink'}`} dir="ltr">
            <span className="truncate">{value}</span>
            {delta && (
              <span
                className={`text-[10px] font-extrabold ${
                  delta.tone === 'up'
                    ? dark
                      ? 'text-aegreen-200'
                      : 'text-aegreen-700'
                    : delta.tone === 'down'
                      ? dark
                        ? 'text-aered-200'
                        : 'text-aered-600'
                      : dark
                        ? 'text-white/50'
                        : 'text-ink-faint'
                }`}
              >
                {delta.tone === 'up' ? '▲' : delta.tone === 'down' ? '▼' : '●'} {delta.text}
              </span>
            )}
          </div>
        </div>
        {spark && spark.length > 1 && (
          <Sparkline values={spark} accent={dark ? '#cba344' : accent} />
        )}
      </div>
      {hint && <div className={`mt-2 text-[10px] font-semibold ${dark ? 'text-white/55' : 'text-ink-faint'}`}>{hint}</div>}
    </div>
  );
}

/** Tiny inline SVG trend line (36×44 viewBox scaled), no axes — pure shape. */
export function Sparkline({
  values,
  accent = '#cba344',
  height = 34,
  width = 72,
}: {
  values: number[];
  accent?: string;
  height?: number;
  width?: number;
}) {
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(1e-9, max - min);
  const points = values
    .map(
      (value, index) =>
        `${(index / (values.length - 1)) * width},${height - 3 - ((value - min) / range) * (height - 6)}`,
    )
    .join(' ');
  const area = `0,${height} ${points} ${width},${height}`;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="shrink-0 self-center"
      aria-hidden
    >
      <polygon points={area} fill={accent} opacity={0.14} />
      <polyline
        points={points}
        fill="none"
        stroke={accent}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* --------------------------------------------------------------------------
 * SectionCard — refined card: slim accent strip, header row, content slot.
 * ------------------------------------------------------------------------- */
export function SectionCard({
  title,
  hint,
  actions,
  children,
  accent,
  className = '',
  padded = true,
  collapseId,
}: {
  title?: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  accent?: string;
  className?: string;
  padded?: boolean;
  /** When set, the card becomes collapsible and persists state in localStorage. */
  collapseId?: string;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    if (!collapseId) return false;
    try {
      return localStorage.getItem(`sai.collapse.${collapseId}`) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () => {
    if (!collapseId) return;
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(`sai.collapse.${collapseId}`, next ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
  };
  return (
    <section className={`overflow-hidden rounded-2xl border border-aeblack-100 bg-white shadow-[0_2px_12px_-6px_rgb(14_15_18/0.1)] ${className}`}>
      {(title || actions) && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 border-b border-aeblack-100 px-5 py-3.5"
          style={accent ? { borderImage: `linear-gradient(to right, ${accent}, transparent) 1` } : undefined}
        >
          <div className="flex items-center gap-2.5">
            {collapseId && (
              <button
                type="button"
                onClick={toggle}
                aria-expanded={!collapsed}
                className="grid h-7 w-7 cursor-pointer place-items-center rounded-lg text-ink-faint transition hover:bg-aeblack-50 hover:text-ink"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                  className={`transition-transform duration-200 ${collapsed ? '-rotate-90' : ''}`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            )}
            {accent && <span aria-hidden className="h-5 w-1 rounded-full" style={{ backgroundColor: accent }} />}
            <div>
              {title && <h2 className="text-sm font-extrabold text-ink">{title}</h2>}
              {hint && <div className="mt-0.5 text-[11px] text-ink-faint">{hint}</div>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {!collapsed && <div className={padded ? 'p-5' : ''}>{children}</div>}
    </section>
  );
}

/* --------------------------------------------------------------------------
 * SegmentedTabs — sliding-thumb tab control with the SAME accessibility
 * contract the classic tab row has: role=tab/tablist, roving tabindex,
 * Home/End/arrow navigation, stable element ids.
 * ------------------------------------------------------------------------- */
export interface SegmentedTabItem {
  id: string;
  label: ReactNode;
  badge?: ReactNode;
}

export function SegmentedTabs({
  tabs,
  active,
  onChange,
  ariaLabel,
  idPrefix,
  onKeyMove,
}: {
  tabs: SegmentedTabItem[];
  active: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  /** Stable id prefix per tab button (e.g. 'agent-tab' → ids agent-tab-overview). */
  idPrefix: string;
  /** Optional keyboard handler hook (receives event + tab id) for custom arrow semantics. */
  onKeyMove?: (event: ReactKeyboardEvent<HTMLButtonElement>, id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const activeButton = container.querySelector<HTMLButtonElement>(`#${CSS.escape(idPrefix)}-${active}`);
      if (!activeButton) return;
      setThumb({ left: activeButton.offsetLeft, width: activeButton.offsetWidth });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [active, tabs.length, idPrefix]);

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      className="relative inline-flex max-w-full flex-wrap items-center gap-1 rounded-2xl border border-aeblack-150 bg-aeblack-50 p-1.5 shadow-inner"
    >
      {thumb && (
        <span
          aria-hidden
          className="absolute top-1.5 bottom-1.5 rounded-xl bg-aeblack-900 shadow-md transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ left: thumb.left, width: thumb.width }}
        />
      )}
      {tabs.map((tab) => {
        const selected = active === tab.id;
        return (
          <button
            key={tab.id}
            id={`${idPrefix}-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${idPrefix.replace(/tab$/, 'panel')}-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => onKeyMove?.(event, tab.id)}
            className={`relative z-10 min-h-10 cursor-pointer rounded-xl px-4 text-xs font-extrabold transition-colors duration-200 ${
              selected ? 'text-white' : 'text-ink-soft hover:text-ink'
            }`}
          >
            {tab.label}
            {tab.badge}
          </button>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * StatusDot — tiny pulsing health dot.
 * ------------------------------------------------------------------------- */
export function StatusDot({ tone }: { tone: 'live' | 'warn' | 'down' | 'idle' }) {
  const color =
    tone === 'live' ? '#3f8e50' : tone === 'warn' ? '#d67907' : tone === 'down' ? '#d83731' : '#9ea2a9';
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      <span
        aria-hidden
        className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-40"
        style={{ backgroundColor: color }}
      />
      <span aria-hidden className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
    </span>
  );
}
